import React, { useState, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { useReactToPrint } from "react-to-print";
import { privateAxios } from "../utils/axios";
import { InvoiceHeader } from "../components/Report-Print/common/InvoiceHeader";
import { InvoiceFooter } from "../components/Report-Print/common/generalInvoiceFooter";

function StockReport() {
  const [stockReportData, setStockReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Stock Report",
  });

  const handleGenerateStockReport = async () => {
    setIsLoading(true);

    try {
      const response = await privateAxios.get(
        `/products/generate-stock-report`
      );

      if (response.data.success) {
        setStockReportData(response.data.data);
      }
    } catch (error) {
      console.error("Error generating stock report:", error);
      toast.error("Failed to generate stock report");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGenerateStockReport();
  }, []);

  const filteredProducts = useMemo(() => {
    if (!stockReportData?.products) return [];

    return stockReportData.products.filter((p) => {
      const keyword = searchTerm.toLowerCase();

      return (
        p.name?.toLowerCase().includes(keyword) ||
        p.category?.toLowerCase().includes(keyword)
      );
    });
  }, [stockReportData, searchTerm]);

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">

        {/* Header */}
        <div className="bg-white rounded-xl border p-6 mt-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-bold text-xl">Stock Report</h1>
              <p className="text-sm text-gray-500">
                Product stock count grouped by product & category
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleGenerateStockReport}
                disabled={isLoading}
                className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50"
              >
                {isLoading ? "Generating..." : "Refresh Report"}
              </button>

              <button
                onClick={handlePrint}
                disabled={!stockReportData || isLoading}
                className="px-4 py-2 border border-black rounded-lg disabled:opacity-50"
              >
                Print
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        {stockReportData && (
          <input
            type="text"
            placeholder="Search by name, category..."
            className="border p-2 rounded-lg w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}

        {/* Table */}
        {stockReportData && (
          <div ref={printRef} className="overflow-x-auto">
            <InvoiceHeader />
            <h2 className="text-center font-bold text-lg my-4">Stock Report</h2>
            <p className="text-right text-sm text-gray-500 mb-2">
              Print Date: {moment().format("DD/MM/YYYY hh:mm A")}
            </p>
            <table className="min-w-full text-sm border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3">#</th>
                  <th className="p-3 text-left">Product</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-right">Product Count</th>
                </tr>
              </thead>

              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr key={`${product.name}-${product.category}`} className="border-t hover:bg-gray-50">
                    <td className="p-3 text-center">{index + 1}</td>

                    <td className="p-3 font-medium">{product.name}</td>

                    <td className="p-3 capitalize">{product.category}</td>

                    <td className="p-3 text-right">{product.productCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <InvoiceFooter />
          </div>
        )}
      </div>
    </div>
  );
}

export default StockReport;
